// src/pages/lead/LeadAnalytics.jsx
// Optimized: uses team-scoped hooks (useTeamMonthlyAttendance) instead
// of useAllMonthlyAttendance + useAllLeaveRequests which fetched the
// entire org's data.  Members and leaves come from LeadContext.
import React, { useMemo } from "react";
import Layout from "../../components/common/Layout";
import { LEAD_NAV } from "../../utils/navigation";
import { useLeadData } from "../../context/LeadContext";
import { useTeamMonthlyAttendance } from "../../hooks/useFirestore";
import {
  MetricCard,
  AttendanceTrendChart,
  TeamStatusSummary,
} from "../../components/analytics/AnalyticsWidgets";
import { isPresent } from "../../utils/attendanceStatus";

function formatDateKey(date) {
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function formatTimeFromMinutes(minutes) {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hrs, mins, 0, 0);
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatWeekLabel(date) {
  return date.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 3);
}

export default function LeadAnalytics() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  // Members + all team leaves come from context — no new listeners created
  const { members, allLeaves, membersLoading, allLeavesLoading } = useLeadData();

  const memberIds = useMemo(() => members.map((m) => m.uid), [members]);

  // Monthly attendance scoped to this team only
  const { data: attendance, loading: attendanceLoading } =
    useTeamMonthlyAttendance(memberIds, year, month);

  const todayIso = formatDateKey(today);

  const weekDays = useMemo(() => {
    const list = [];
    for (let i = 6; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      list.push({ iso: formatDateKey(date), label: formatWeekLabel(date) });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayIso]);

  const weeklyData = useMemo(() => {
    return weekDays.map((day) => {
      const dailyRecords = attendance.filter((item) => item.date === day.iso);
      const present = dailyRecords.filter(
        (item) => item.mode !== "remote" && isPresent(item),
      ).length;
      const remote = dailyRecords.filter((item) => item.mode === "remote").length;
      const leave = Math.max(
        0,
        dailyRecords.filter((item) => item.status === "leave").length +
          allLeaves.filter(
            (r) =>
              r.status === "approved" &&
              r.fromDate <= day.iso &&
              r.toDate >= day.iso,
          ).length,
      );
      const ignoredCount = members.filter((m) => {
        const hasAtt = dailyRecords.some((a) => a.userId === m.uid && isPresent(a));
        if (hasAtt) return false;
        const empLeaves = allLeaves.filter(
          (lr) => lr.userId === m.uid && lr.fromDate <= day.iso && lr.toDate >= day.iso
        );
        return empLeaves.some(
          (lr) => lr.status !== "approved" && (lr.status === "pending_lead" || lr.status === "pending_admin" || lr.status === "rejected" || lr.status === "cancelled")
        );
      }).length;
      const absent = Math.max(0, members.length - present - remote - leave - ignoredCount);
      return { day: day.label, present, remote, leave, absent };
    });
  }, [weekDays, attendance, allLeaves, members, members.length]);

  const statusCounts = useMemo(() => {
    const todayRecords = attendance.filter((item) => item.date === todayIso);
    const present = todayRecords.filter(
      (item) => item.mode !== "remote" && isPresent(item),
    ).length;
    const remote = todayRecords.filter((item) => item.mode === "remote").length;
    const leave = Math.max(
      0,
      todayRecords.filter((item) => item.status === "leave").length +
        allLeaves.filter(
          (r) =>
            r.status === "approved" &&
            r.fromDate <= todayIso &&
            r.toDate >= todayIso,
        ).length,
    );
    const ignoredCount = members.filter((m) => {
      const hasAtt = todayRecords.some((a) => a.userId === m.uid && isPresent(a));
      if (hasAtt) return false;
      const empLeaves = allLeaves.filter(
        (lr) => lr.userId === m.uid && lr.fromDate <= todayIso && lr.toDate >= todayIso
      );
      return empLeaves.some(
        (lr) => lr.status !== "approved" && (lr.status === "pending_lead" || lr.status === "pending_admin" || lr.status === "rejected" || lr.status === "cancelled")
      );
    }).length;
    const absent = Math.max(0, members.length - present - remote - leave - ignoredCount);
    return { present, remote, leave, absent };
  }, [attendance, allLeaves, members, todayIso]);

  const recentAttendance = useMemo(
    () => attendance.filter((item) => weekDays.some((d) => d.iso === item.date)),
    [attendance, weekDays],
  );

  const avgCheckInTime = useMemo(() => {
    const valid = recentAttendance.filter((item) => item.checkIn);
    if (valid.length === 0) return "—";
    const total = valid.reduce((sum, item) => {
      const t = item.checkIn.toDate();
      return sum + t.getHours() * 60 + t.getMinutes();
    }, 0);
    return formatTimeFromMinutes(Math.round(total / valid.length));
  }, [recentAttendance]);

  const avgWorkHours = useMemo(() => {
    const valid = recentAttendance.filter((item) => item.checkIn && item.checkOut);
    if (valid.length === 0) return "—";
    const total = valid.reduce((sum, item) => {
      const h = item.hoursWorked
        ? item.hoursWorked
        : (item.checkOut.toDate() - item.checkIn.toDate()) / 3_600_000;
      return sum + h;
    }, 0);
    return `${(total / valid.length).toFixed(1)} hrs`;
  }, [recentAttendance]);

  const leaveApprovalRate = useMemo(() => {
    if (allLeaves.length === 0) return "—";
    const approved = allLeaves.filter((r) => r.status === "approved").length;
    return `${Math.round((approved / allLeaves.length) * 100)}%`;
  }, [allLeaves]);

  const loading = membersLoading || allLeavesLoading || attendanceLoading;

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Lead Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">
            Team attendance trends and leave analytics
          </p>
        </div>
      </div>

      <div
        className="summary-grid"
        style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          marginBottom: "1.25rem",
        }}
      >
        <MetricCard title="Team Punch-in Avg" value={avgCheckInTime} loading={loading} />
        <MetricCard title="Avg Work Hours" value={avgWorkHours} loading={loading} />
        <MetricCard title="Leave Approval" value={leaveApprovalRate} loading={loading} />
      </div>

      <div
        className="analytics-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "1.25rem",
        }}
      >
        <AttendanceTrendChart data={weeklyData} loading={loading} />
        <TeamStatusSummary counts={statusCounts} loading={loading} />
      </div>
    </Layout>
  );
}
